/** Small display pieces shared by the template and partial pages. */
import { useQuery } from '@tanstack/react-query'
import { Library } from 'lucide-react'
import { allApplicationsQuery } from '@/api/queries/applications'
import type { TemplateFormat } from '@/api/types'
import { IdCrumb } from '@/shell/crumbs'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/cn'
import { partialQuery, templateQuery } from './api'

export function FormatBadge({ format }: { format: TemplateFormat }) {
  return (
    <Badge tone="outline" className="font-mono tracking-wide uppercase">
      {format === 'mjml' ? 'MJML' : 'HTML'}
    </Badge>
  )
}

/** Application name for an owner ID, or "Shared library" for null. */
export function useOwnerName(applicationId: string | null | undefined): string | undefined {
  const apps = useQuery({ ...allApplicationsQuery, enabled: !!applicationId })
  if (!applicationId) return 'Shared library'
  return apps.data?.find((a) => a.id === applicationId)?.name
}

export function OwnerLabel({
  applicationId,
  className,
}: {
  applicationId: string | null | undefined
  className?: string
}) {
  const name = useOwnerName(applicationId)
  if (!applicationId)
    return (
      <span className={cn('inline-flex items-center gap-1 text-ink-2', className)}>
        <Library className="size-3.5 text-brass" aria-hidden />
        Shared library
      </span>
    )
  return (
    <span className={cn('truncate', className)} title={applicationId}>
      {name ?? <span className="font-mono text-xs text-ink-3">{applicationId}</span>}
    </span>
  )
}

/** Breadcrumb that shows the template's name once loaded. */
export function TemplateCrumb({ params }: { params: Record<string, string> }) {
  const id = params.templateId ?? ''
  const q = useQuery({ ...templateQuery(id), enabled: !!id })
  return q.data ? <span>{q.data.name}</span> : <IdCrumb value={id} />
}

/** Breadcrumb that shows the partial's key once loaded. */
export function PartialCrumb({ params }: { params: Record<string, string> }) {
  const id = params.partialId ?? ''
  const q = useQuery({ ...partialQuery(id), enabled: !!id })
  return q.data ? <span className="font-mono text-xs">{q.data.key}</span> : <IdCrumb value={id} />
}

/** Owner options for pickers: the shared library plus every application. */
export const SHARED_OWNER = '__shared__'

export function useOwnerOptions() {
  const apps = useQuery(allApplicationsQuery)
  const options = [
    { value: SHARED_OWNER, label: 'Shared library', hint: 'Available to every application' },
    ...(apps.data ?? []).map((a) => ({ value: a.id, label: a.name, hint: a.slug })),
  ]
  return { options, isLoading: apps.isPending, apps: apps.data ?? [] }
}
